import { ParsedUrlQuery } from "querystring";

import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider, Theme } from "@mui/material/styles";
import { FormType, Locales } from "@simplyk/common";
import { useSearchParams } from "next/navigation";
import { useRouter } from "next/router";

import { isProduction } from "../../../constants/env";
import { LocaleProvider } from "../../../contexts/LocaleContext";
import { GetCurrentUserFrontendObject } from "../../../contexts/types";
import { RedirectOnUnsupportedFormsBrowser } from "../../../features/UnsupportedBrowser/RedirectOnUnsupportedFormsBrowser";
import { useLogParamsEvent } from "../../../hooks/amplitude/useLogParamsEvent";
import { useDisableSafariBackForwardCache } from "../../../hooks/useDisableSafariBackForwardCache";
import { useLocaleRedirection } from "../../../hooks/useLocaleRedirection";
import { MinimalLayout } from "../../../layouts/MinimalLayout";
import { AmplitudeProvider } from "../../../providers/AmplitudeProvider";
import { FrontendFormProvider } from "../../../providers/FrontendFormProvider";
import { PageLoadingProvider } from "../../../providers/PageLoadingProvider";
import { zeffyTheme } from "../../../theme/theme";
import { DonationFormFrontendPageOutput, TicketingFrontendPageOutput } from "../../../types/trpc";
import { DevContextCopy } from "../../DevContextCopy/DevContextCopy";
import { HubspotChatbot } from "../../HubspotChatbot/HubspotChatbot";
import { NotificationBannerHeightProvider } from "../../NotificationBanners/NotificationBannerHeightContext";
import { PublicRoute } from "../../Routes/PublicRoute";
import { UserConsent } from "../../UserConsent/UserConsent";

import "react-phone-input-2/lib/material.css";
import "react-phone-input-2/lib/style.css";

import { ZeffySnackbarProvider } from "@/dashboard-design-system/Snackbar";

export const AppFormProvider = ({
  children,
  theme,
  skipUserConsentModal,
  organization,
  fields,
  formData,
  formType,
  isEmbed,
  isFundraiser,
  shouldDisplayLogin,
  themeColor,
}: {
  children: React.ReactNode;
  theme: Theme;
  themeColor: string | null;
  organization: GetCurrentUserFrontendObject["currentOrganization"];
  fields: { locale: Locales }[];
  formData: DonationFormFrontendPageOutput | TicketingFrontendPageOutput | null;
  formType: FormType | null;
  isEmbed?: boolean;
  isFundraiser?: boolean;
  shouldDisplayLogin?: boolean;
  skipUserConsentModal?: boolean;
}) => {
  useLogParamsEvent();
  useDisableSafariBackForwardCache();
  useLocaleRedirection({ country: organization?.country, fields });
  const router = useRouter();
  const searchParams = useSearchParams();
  const query = Object.fromEntries(searchParams.entries());

  return (
    <>
      {/* for router ready, see https://github.com/vercel/next.js/discussions/11484#discussioncomment-356055 */}
      <PageLoadingProvider isLoading={Boolean(!router.isReady || router.isFallback)} disableLoading>
        <ThemeProvider theme={theme || zeffyTheme}>
          <NotificationBannerHeightProvider>
            <CssBaseline />
            <AmplitudeProvider>
              <LocaleProvider>
                <RedirectOnUnsupportedFormsBrowser>
                  <ZeffySnackbarProvider>
                    <DevContextCopy formId={formData?.id || null} />
                    <PublicRoute layout={(props) => <MinimalLayout hideBackground {...props} />} noScreenPadding={true}>
                      <FrontendFormProvider
                        formData={formData}
                        organization={organization}
                        formType={formType}
                        isEmbed={isEmbed || false}
                        isFundraiser={isFundraiser || false}
                        shouldDisplayLogin={shouldDisplayLogin || false}
                        themeColor={themeColor || ""}
                      >
                        {children}
                      </FrontendFormProvider>
                    </PublicRoute>
                    {!isProduction && !hideHubspotChatbot(query, router.route) && <HubspotChatbot />}
                    <UserConsent skipModal={skipUserConsentModal} />
                  </ZeffySnackbarProvider>
                </RedirectOnUnsupportedFormsBrowser>
              </LocaleProvider>
            </AmplitudeProvider>
          </NotificationBannerHeightProvider>
        </ThemeProvider>
      </PageLoadingProvider>
    </>
  );
};

const HubspotChatflowTargets = {
  show: new Set(["o", "login", "register", "d"]),
  hide: new Set(["donation-form", "ticketing", "team", "peer-to-peer", "fundraising", "embed"]),
};

const hideHubspotChatbot = (query: ParsedUrlQuery, route: string) => {
  // Chatbot is hidden on form creation and edition
  if (Object.keys(query).length > 0 && (route.includes("/forms/edit") || route.includes("/forms/new"))) {
    return true;
  }

  // get first level path
  const path = route.split("/")[1];
  if (HubspotChatflowTargets.hide.has(path)) {
    return true;
  }

  return query.hideHubspotChatbot === "true" || !HubspotChatflowTargets.show.has(path);
};
